import { useEffect, useMemo, useState } from 'react';

import { useEtalonnage, useEtalonner } from '../api/queries';
import type { MethodeKv, SessionEtalonnage } from '../api/types';
import { Capacite } from '../auth/permissions';
import { usePeut } from '../auth/useSession';
import { Badge, BadgeSynchronisation } from './Badges';
import { Bouton } from './Boutons';
import { Carte, Definitions } from './Carte';
import { Saisie, Selecteur } from './Champs';
import { Chargement } from './Chargement';
import { MessageErreur } from './MessageErreur';
import { useNotifications } from './Notifications';
import { VALEUR_ABSENTE } from '../i18n/fr';
import { formaterDateHeure, formaterDecimal } from '../lib/format';
import type { StatutSynchronisation } from '@genviz/shared/enums';

/** Relevés d'oscilloscope saisis, gardés dans ce navigateur le temps de la campagne. */
const CLE_RELEVES = 'gv-etalonnage-releves';

function lireReleves(): Record<string, string> {
  try {
    const brut = window.localStorage.getItem(CLE_RELEVES);
    const valeur: unknown = brut ? JSON.parse(brut) : {};
    return valeur !== null && typeof valeur === 'object' ? (valeur as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function ecrireReleves(releves: Record<string, string>): void {
  try {
    window.localStorage.setItem(CLE_RELEVES, JSON.stringify(releves));
  } catch {
    /* stockage indisponible : les relevés restent valables pour la page ouverte */
  }
}

const LIBELLE_METHODE: Record<string, string> = {
  pic: 'pic',
  niveau: 'niveau 80–400 µs',
  aire: 'aire (ancien)',
};

/** Grandeur de la session pour une méthode donnée. */
function grandeur(s: SessionEtalonnage, methode: MethodeKv): number {
  return (methode === 'niveau' ? s.niveau_moyen : s.pic_moyen) ?? 0;
}

function lireNombre(texte: string | undefined): number | null {
  if (texte === undefined) return null;
  const n = Number(texte.replace(',', '.').trim());
  return texte.trim() !== '' && Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Étalonnage de la tension d'un appareil.
 *
 * Depuis le firmware 2.5.1, la carte calcule kV = coefficient × pic (LSB) et reçoit ce coefficient
 * par `dn/cfg`. Il dépend du montage de l'électrode sur le câble HT et du modèle d'électrificateur.
 * L'opérateur rapproche les sessions de mesure précise des crêtes lues à l'oscilloscope ; la carte
 * propose le coefficient des moindres carrés passant par l'origine, k = Σ(kV·pic) / Σ(pic²).
 */
export function EtalonnageTension({ numeroSerie }: { numeroSerie: string }): JSX.Element {
  const etalonnage = useEtalonnage(numeroSerie);
  const mutation = useEtalonner(numeroSerie);
  const notifications = useNotifications();
  const peutEtalonner = usePeut(Capacite.ETALONNER);

  const [releves, setReleves] = useState<Record<string, string>>(lireReleves);
  const [coefficient, setCoefficient] = useState('');
  const [motif, setMotif] = useState('');
  const [toutes, setToutes] = useState(false);
  const [methode, setMethode] = useState<MethodeKv | null>(null);
  const methodeChoisie: MethodeKv = methode ?? etalonnage.data?.kv_methode ?? 'pic';

  useEffect(() => {
    ecrireReleves(releves);
  }, [releves]);

  const sessions = useMemo(() => etalonnage.data?.sessions ?? [], [etalonnage.data]);

  const ajustement = useMemo(() => {
    const points = sessions
      .map((s) => ({ s, kv: lireNombre(releves[s.commande_id]) }))
      .filter((p): p is { s: SessionEtalonnage; kv: number } => p.kv !== null && grandeur(p.s, methodeChoisie) > 0);
    if (points.length === 0) return null;
    let sxy = 0;
    let sxx = 0;
    for (const p of points) {
      const x = grandeur(p.s, methodeChoisie);
      sxy += p.kv * x;
      sxx += x * x;
    }
    const k = sxy / sxx;
    const ecartMax = Math.max(...points.map((p) => Math.abs(k * grandeur(p.s, methodeChoisie) - p.kv)));
    return { k, n: points.length, ecartMax };
  }, [sessions, releves, methodeChoisie]);

  if (etalonnage.isPending) return <Chargement libelle="Étalonnage…" />;
  if (etalonnage.error) return <MessageErreur erreur={etalonnage.error} />;
  const e = etalonnage.data;

  // Estimation avec la méthode et le coefficient choisis : la saisie en cours, sinon le réglage
  // demandé s'il porte sur la même méthode, sinon ce que la carte annonce.
  const saisiPourEstimer = lireNombre(coefficient);
  const kCourant =
    saisiPourEstimer ??
    (methodeChoisie === e.kv_methode && e.kv_par_lsb !== null
      ? e.kv_par_lsb
      : e.observe?.methode === methodeChoisie
        ? e.observe.k
        : null);
  const visibles = toutes ? sessions : sessions.slice(0, 20);
  const saisi = lireNombre(coefficient);
  const motifValide = motif.trim().length >= 3;

  const envoyer = (valeur: number | null): void => {
    mutation.mutate(
      { kv_par_lsb: valeur, kv_methode: methodeChoisie, motif: motif.trim() },
      {
        onSuccess: (r) => {
          notifications.succes(
            r.configuration_publiee
              ? `Coefficient envoyé à la carte (configuration v${r.version}).`
              : `Coefficient enregistré (v${r.version}), configuration non publiée : elle partira à la prochaine connexion.`,
          );
          setCoefficient('');
          setMotif('');
        },
      },
    );
  };

  return (
    <Carte
      titre="Étalonnage de la tension"
      sousTitre="kV = coefficient × pic ou niveau moyen 80–400 µs · méthode réglable dès le firmware 2.6.0"
      actions={
        e.statut_sync ? <BadgeSynchronisation statut={e.statut_sync as StatutSynchronisation} /> : null
      }
    >
      <Definitions
        entrees={[
          {
            cle: 'Réglage demandé',
            valeur: `${LIBELLE_METHODE[e.kv_methode] ?? e.kv_methode} × ${
              e.kv_par_lsb === null ? 'valeur du firmware' : `${formaterDecimal(e.kv_par_lsb, 4)} kV par LSB`
            }`,
          },
          {
            cle: 'Annoncé par la carte',
            valeur: e.observe ? (
              <>
                {formaterDecimal(e.observe.k, 4)} ·{' '}
                <Badge
                  ton={e.observe.methode === 'aire' ? 'orange' : 'vert'}
                  libelle={`calcul sur ${LIBELLE_METHODE[e.observe.methode] ?? e.observe.methode}`}
                />{' '}
                <span className="gv-secondaire">dernière mesure précise {formaterDateHeure(e.observe.at)}</span>
              </>
            ) : (
              'aucune mesure précise'
            ),
          },
          {
            cle: 'Version de configuration',
            valeur:
              e.version === null
                ? VALEUR_ABSENTE
                : `souhaitée v${e.version} · appliquée v${e.version_appliquee ?? '–'}`,
          },
        ]}
      />

      {peutEtalonner ? (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 16 }}>
          <Selecteur
            label="Méthode"
            options={[
              { valeur: 'pic', libelle: 'Pic (impulsion brève : Challenge…)' },
              { valeur: 'niveau', libelle: 'Niveau 80–400 µs (plateau : EP5000…)' },
            ]}
            value={methodeChoisie}
            onChange={(ev) => {
              setMethode(ev.currentTarget.value === 'niveau' ? 'niveau' : 'pic');
            }}
          />
          <Saisie
            label="Nouveau coefficient (kV par LSB)"
            inputMode="decimal"
            placeholder="0,0765"
            value={coefficient}
            onChange={(ev) => {
              setCoefficient(ev.currentTarget.value);
            }}
            erreur={coefficient !== '' && (saisi === null || saisi > 5) ? 'Nombre entre 0 et 5' : null}
          />
          <Saisie
            label="Motif (journal d’audit)"
            placeholder="Challenge 3000, oscilloscope 21/10"
            value={motif}
            maxLength={200}
            onChange={(ev) => {
              setMotif(ev.currentTarget.value);
            }}
          />
          <Bouton
            variante="primaire"
            disabled={saisi === null || saisi > 5 || !motifValide}
            occupe={mutation.isPending}
            onClick={() => {
              envoyer(saisi);
            }}
          >
            Envoyer à la carte
          </Bouton>
          <Bouton
            variante="discret"
            disabled={(e.kv_par_lsb === null && methodeChoisie === e.kv_methode) || !motifValide}
            occupe={mutation.isPending}
            onClick={() => {
              envoyer(null);
            }}
          >
            Coefficient par défaut du firmware
          </Bouton>
        </div>
      ) : null}
      {mutation.error ? <MessageErreur erreur={mutation.error} /> : null}

      <h3 className="gv-corps-s" style={{ marginTop: 24, marginBottom: 8, fontWeight: 700 }}>
        Calcul à partir des relevés d’oscilloscope
      </h3>
      <p className="gv-corps-s gv-secondaire" style={{ marginTop: 0 }}>
        Saisissez la crête lue à l’oscilloscope en face des sessions de mesure précise, sans avoir
        déplacé l’électrode entre elles. Les relevés restent dans ce navigateur.
      </p>

      {ajustement ? (
        <div className="gv-bandeau gv-bandeau--info" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>
            Coefficient calculé sur {ajustement.n} session{ajustement.n > 1 ? 's' : ''} :{' '}
            <strong className="gv-mono">{formaterDecimal(ajustement.k, 4)}</strong> · écart maximal{' '}
            {formaterDecimal(ajustement.ecartMax, 2)} kV
          </span>
          {peutEtalonner ? (
            <Bouton
              petit
              onClick={() => {
                setCoefficient(formaterDecimal(ajustement.k, 4));
              }}
            >
              Utiliser ce coefficient
            </Bouton>
          ) : null}
        </div>
      ) : null}

      {sessions.length === 0 ? (
        <p className="gv-corps-s gv-secondaire">Aucune session de mesure précise sur cet appareil.</p>
      ) : (
        <div className="gv-table-cadre" style={{ marginTop: 12 }}>
          <table className="gv-table">
            <thead>
              <tr>
                <th>Session</th>
                <th>Nom</th>
                <th>Impulsions</th>
                <th>Pic (LSB)</th>
                <th>Niveau 80–400 µs</th>
                <th>Estimé (kV)</th>
                <th>Oscilloscope (kV)</th>
                <th>kV / LSB</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((s) => {
                const kv = lireNombre(releves[s.commande_id]);
                const x = grandeur(s, methodeChoisie);
                return (
                  <tr key={s.commande_id}>
                    <td className="gv-mono">{formaterDateHeure(s.debut_at)}</td>
                    <td>{s.nom ?? <span className="gv-secondaire">sans nom</span>}</td>
                    <td className="gv-mono">{s.nb}</td>
                    <td className="gv-mono" style={methodeChoisie === 'pic' ? { fontWeight: 700 } : undefined}>
                      {formaterDecimal(s.pic_moyen, 0)}
                      {s.pic_ecart !== null ? ` ±${formaterDecimal(s.pic_ecart, 0)}` : ''}
                    </td>
                    <td className="gv-mono" style={methodeChoisie === 'niveau' ? { fontWeight: 700 } : undefined}>
                      {formaterDecimal(s.niveau_moyen, 1)}
                      {s.niveau_ecart !== null ? ` ±${formaterDecimal(s.niveau_ecart, 1)}` : ''}
                    </td>
                    <td className="gv-mono">
                      {kCourant !== null && x > 0 ? formaterDecimal(kCourant * x, 2) : VALEUR_ABSENTE}
                    </td>
                    <td>
                      <input
                        className="gv-saisie"
                        style={{ width: 90 }}
                        inputMode="decimal"
                        aria-label={`Crête oscilloscope, session du ${formaterDateHeure(s.debut_at)}`}
                        value={releves[s.commande_id] ?? ''}
                        onChange={(ev) => {
                          const valeur = ev.currentTarget.value;
                          setReleves((avant) => {
                            const suite = { ...avant };
                            if (valeur.trim() === '') delete suite[s.commande_id];
                            else suite[s.commande_id] = valeur;
                            return suite;
                          });
                        }}
                      />
                    </td>
                    <td className="gv-mono">{kv !== null && x > 0 ? formaterDecimal(kv / x, 4) : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {sessions.length > 20 ? (
        <Bouton
          variante="discret"
          petit
          onClick={() => {
            setToutes((v) => !v);
          }}
        >
          {toutes ? 'Afficher les 20 dernières' : `Afficher les ${sessions.length} sessions`}
        </Bouton>
      ) : null}
    </Carte>
  );
}
